import {
  Box,
  Button,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { getVersions as getAdGroupsVersion } from '../api/ventura/adGroup';
import { getVersions as getContractVersions } from '../api/ventura/contract';
import { getVersions as getCreativesVersions } from '../api/ventura/creative';
import { getVersions as getLineItemsVersions } from '../api/ventura/lineItem';
import CollapsiblePaper from '../components/shared/CollapsiblePaper';
import { useConfig } from './useConfig';
import useModal from './useModal';

// Component that renders a table with synced horizontal scrollbars at top and bottom
function DualScrollTableContainer({ children }: { children: React.ReactNode }) {
  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const isScrollingSyncRef = useRef(false);
  const lastWidthRef = useRef(0);

  // Use ResizeObserver to detect content size changes
  useEffect(() => {
    const contentEl = contentRef.current;
    if (!contentEl) return undefined;

    const updateWidth = () => {
      const newWidth = contentEl.scrollWidth;
      if (newWidth !== lastWidthRef.current) {
        lastWidthRef.current = newWidth;
        setContentWidth(newWidth);
      }
    };

    // Initial measurement
    updateWidth();

    // Observe size changes
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(contentEl);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Sync scroll positions with guard against infinite loop
  const handleTopScroll = useCallback(() => {
    if (isScrollingSyncRef.current) return;
    isScrollingSyncRef.current = true;
    if (topScrollRef.current && bottomScrollRef.current) {
      bottomScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
    requestAnimationFrame(() => {
      isScrollingSyncRef.current = false;
    });
  }, []);

  const handleBottomScroll = useCallback(() => {
    if (isScrollingSyncRef.current) return;
    isScrollingSyncRef.current = true;
    if (topScrollRef.current && bottomScrollRef.current) {
      topScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
    }
    requestAnimationFrame(() => {
      isScrollingSyncRef.current = false;
    });
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        ref={topScrollRef}
        onScroll={handleTopScroll}
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          height: 16,
        }}
      >
        <Box sx={{ width: contentWidth, height: 1 }} />
      </Box>
      <Box
        ref={bottomScrollRef}
        onScroll={handleBottomScroll}
        sx={{ overflowX: 'auto' }}
      >
        <Box ref={contentRef}>{children}</Box>
      </Box>
    </Box>
  );
}

export enum ModelType {
  Contract,
  LineItem,
  AdGroup,
  AdGroupTargeting,
  Creative,
  TargetedCreative,
}

interface VersionsProps {
  modelType: ModelType;
  modelId: number;
  page: number;
  perPage: number;
  options: object;
}

function Versions(props: VersionsProps) {
  const { modelType, modelId, page: pageParam, perPage: perPageParam, options } = props;

  const [page, setPage] = useState(pageParam);
  const [perPage, setPerPage] = useState(perPageParam);
  const [whodunnitFilter, setWhodunnitFilter] = useState([]);
  const [changedFieldsFilter, setChangedFieldsFilter] = useState([]);

  const { apiHost, apiHostFor } = useConfig();

  // ONLY the contract changelog (GET /admin/contracts/{id}/versions[_meta])
  // opts into the per-domain flip (ACH-colk) — ACH's versions domain mounts
  // just those two routes (versions/register.go). The line-item / ad-group /
  // creative changelogs are NOT ACH-served and stay on the Rails apiHost.
  // The envelopes are shape-identical on both backends ({collection,pagination}
  // + {whodunnit,object_changes} meta; ACH pins even the page-less pagination
  // shape and PST created_at strings), and ACH binds the FE's bracketed
  // whodunnit[]/changed_fields[] filter params (G5) — so only the host switches.
  const contractVersionsHost = apiHostFor('versions');

  const queryKey = [modelType.toString(), modelId, page, perPage, whodunnitFilter, changedFieldsFilter];
  let queryFn;
  const filters = {
    'whodunnit[]': whodunnitFilter,
    'changed_fields[]': changedFieldsFilter,
  };
  switch (modelType) {
    case ModelType.Contract:
      queryFn = () => getContractVersions(contractVersionsHost, modelId, page, perPage, filters);
      break;
    case ModelType.LineItem:
      queryFn = () => getLineItemsVersions(apiHost, modelId, page, perPage, filters);
      break;
    case ModelType.AdGroup:
      queryFn = () => getAdGroupsVersion(apiHost, modelId, page, perPage, 'Campaign', filters);
      break;
    case ModelType.AdGroupTargeting:
      queryFn = () => getAdGroupsVersion(apiHost, modelId, page, perPage, 'TargetingProfile', filters);
      break;
    case ModelType.Creative: {
      const targetedCreativeId = get(options, 'targetedCreativeId');
      queryFn = () => getCreativesVersions(apiHost, modelId, targetedCreativeId, page, perPage, 'Creative', filters);
      break;
    }
    case ModelType.TargetedCreative: {
      const targetedCreativeId = get(options, 'targetedCreativeId');
      queryFn = () =>
        getCreativesVersions(apiHost, modelId, targetedCreativeId, page, perPage, 'TargetedCreative', filters);
      break;
    }
    default:
      break;
  }

  const query: any = useQuery({
    queryKey,
    queryFn,
  });

  const { isLoading, isSuccess, data } = query;

  const handlePageChange = (_event: unknown, value: number) => {
    setPage(value);
  };

  const handlePerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPerPage(Number(event.target.value));
    setPage(0);
  };

  const metaData = data?.meta?.whodunnit?.collection.sort() || [];
  // Get an array of changed fields by filtering out all the unique changes in the object.
  const changedFieldData =
    data?.meta?.object_changes?.collection
      .reduce((accumulator, currentValue) => {
        Object.keys(currentValue.object_changes || {}).forEach((item) => {
          if (!accumulator.includes(item)) {
            accumulator.push(item);
          }
        });
        return accumulator;
      }, [])
      .sort() || [];

  const prettyFilter = useCallback(
    (obj) => {
      if (obj === null || obj === undefined) return '';
      if (typeof obj === 'object' || Array.isArray(obj)) return JSON.stringify(obj);
      return String(obj);
    },
    []
  );

  const filterCount = whodunnitFilter.length + changedFieldsFilter.length;

  return (
    <Stack alignItems="flex-start" spacing={2} padding={2}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Changelog
      </Typography>
      <CollapsiblePaper title={`Filters (${filterCount})`}>
        <Stack spacing={2}>
          {!isEmpty(metaData) && (
            <>
              <Typography>Who</Typography>
              <Stack direction="row">
                {metaData.map((item) => (
                  <Chip
                    key={item.whodunnit}
                    sx={{ margin: '0 0.5rem 0.5rem 0' }}
                    variant="outlined"
                    color={whodunnitFilter.includes(item.whodunnit) ? 'success' : 'default'}
                    label={<Typography>{item.whodunnit_name}</Typography>}
                    onClick={() =>
                      setWhodunnitFilter(
                        whodunnitFilter.includes(item.whodunnit)
                          ? whodunnitFilter.filter((item2) => item2 !== item.whodunnit)
                          : [...whodunnitFilter, item.whodunnit]
                      )
                    }
                  />
                ))}
              </Stack>
            </>
          )}
          {!isEmpty(changedFieldData) && (
            <>
              <Typography>Changed Fields</Typography>
              <Stack direction="row" flexWrap="wrap">
                {changedFieldData.map((item) => (
                  <Chip
                    key={item}
                    sx={{ margin: '0 0.5rem 0.5rem 0' }}
                    variant="outlined"
                    color={changedFieldsFilter.includes(item) ? 'success' : 'default'}
                    label={<Typography>{item}</Typography>}
                    onClick={() =>
                      setChangedFieldsFilter(
                        changedFieldsFilter.includes(item)
                          ? changedFieldsFilter.filter((item2) => item2 !== item)
                          : [...changedFieldsFilter, item]
                      )
                    }
                  />
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </CollapsiblePaper>
      <DualScrollTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 125 }}>
                <Typography>Who</Typography>
              </TableCell>
              <TableCell sx={{ minWidth: 125 }}>
                <Typography>When</Typography>
              </TableCell>
              <TableCell sx={{ minWidth: 125 }} valign="top">
                <Grid container>
                  <Grid item xs={4}>
                    <Typography>Changed Field</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>Old</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>New</Typography>
                  </Grid>
                </Grid>
              </TableCell>
              <TableCell sx={{ minWidth: 125 }}>
                <Typography>Reason</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading &&
              [...Array(perPage)].map((_, rowIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={`skeleton-row-${rowIndex}`} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  {[...Array(4)].map((__, colIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableCell key={`skeleton-cell-${rowIndex}-${colIndex}`} scope="row">
                      <Skeleton animation="wave" variant="text" sx={{ fontSize: '1rem' }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {isSuccess && (
              <TableRow key="pagination-1">
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  count={data.pagination?.total_items || 0}
                  page={page}
                  onPageChange={handlePageChange}
                  rowsPerPage={perPage}
                  onRowsPerPageChange={handlePerPageChange}
                />
              </TableRow>
            )}
            {isSuccess &&
              data.collection?.map((item) => (
                <TableRow key={item.id}>
                  <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word', minWidth: 125, maxWidth: 200 }}>
                    <Typography sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{item.whodunnit_name}</Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', minWidth: 125 }}>
                    <Typography>{item.created_at}</Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', minWidth: 125 }}>
                    {item.object_changes &&
                      Object.keys(item.object_changes).map((key) => (
                        <Grid container key={key} sx={{ width: '600px' }}>
                          <Grid item sx={{ width: '33%' }}>
                            <TextField sx={{ width: '95%' }} InputProps={{ readOnly: true }} value={key} />
                          </Grid>
                          <Grid item sx={{ width: '33%' }}>
                            <TextField
                              sx={{ width: '95%' }}
                              InputProps={{ readOnly: true }}
                              value={prettyFilter(item.object_changes[key][0])}
                            />
                          </Grid>
                          <Grid item sx={{ width: '33%' }}>
                            <TextField
                              sx={{ width: '95%' }}
                              InputProps={{ readOnly: true }}
                              value={prettyFilter(item.object_changes[key][1])}
                            />
                          </Grid>
                        </Grid>
                      ))}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', minWidth: 125 }}>
                    <TextField InputProps={{ readOnly: true }} value={item.reason ?? ''} />
                  </TableCell>
                </TableRow>
              ))}
            {isSuccess && (
              <TableRow key="pagination-2">
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  count={data.pagination?.total_items || 0}
                  page={page}
                  onPageChange={handlePageChange}
                  rowsPerPage={perPage}
                  onRowsPerPageChange={handlePerPageChange}
                />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </DualScrollTableContainer>
    </Stack>
  );
}

function useVersions(
  modelType: ModelType,
  modelId: number,
  page: number = 0,
  perPage: number = 10,
  options: object = {}
) {
  const [openVersionsModal, closeVersionsModal] = useModal(() => (
    <>
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: 2 }}>
        <Versions modelType={modelType} modelId={modelId} page={page} perPage={perPage} options={options} />
      </Box>
      <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', p: 2 }}>
        <Stack direction="row" justifyContent="center" width="100%">
          <Button onClick={closeVersionsModal}>Close</Button>
        </Stack>
      </Box>
    </>
  ));

  return {
    openVersionsModal,
    closeVersionsModal,
  };
}

export default useVersions;
